import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, CheckCircle2 } from 'lucide-react';
import client from '../../api/client';
import { appInsights } from '../../lib/appInsights';
import { useAuth } from '../../context/AuthContext';
import PTOBalanceCard from '../components/PTOBalanceCard';
import SearchableSelect from '../../shared/components/SearchableSelect';
import { formatDateSafe } from '../../utils/formatDateSafe';
import { NAVY, AMBER, BORDER_COLOR, bubbleStyle, navyBtn, fieldStyle, selectStyle } from '../../styles/tokens';

import { etiquetaProyecto } from '../../utils/formatProject';
/**
 * NUEVA SOLICITUD DE AUSENCIA — reescrita en V62.
 *
 * La versión anterior elegía un SUPERVISOR (el L1 de la cadena fija de 5 niveles) y
 * trabajaba en DÍAS. El modelo nuevo elige PROYECTO (que es de donde sale la cadena:
 * Ingeniero L1 → Socio L2, V59/V60) y PERSONA, y trabaja en HORAS.
 *
 * ⚠⚠ EL CASO NORMAL ES CARGAR POR OTRO, NO POR UNO MISMO: de 152 personas activas solo 3
 * tienen cuenta de usuario. Por eso el sujeto de la ausencia es un campo de primera clase
 * y no se asume que sea quien está logueado.
 *
 * ⚠ Orden de los campos: cada uno acota al siguiente. El proyecto define la cadena Y la
 * lista de personas; la persona define el saldo.
 */

const TYPE_OPTIONS = [
  { value: 'vacation', label: 'Vacaciones' },
  { value: 'sick',     label: 'Enfermedad' },
  // ⚰ 'personal' RETIRADO: V59 lo sacó del CHECK de pto_requests (no existe en
  // pto_balance_hours, ni en los topes del motor, ni en el Excel de la contable). Ofrecerlo
  // era mostrar un tipo que la base rechaza con 400.
];

const HORAS_POR_DIA = 8;

/**
 * Días hábiles del rango: Lun-Vie menos feriados activos.
 * ⚠ Espeja `businessDays.countBusinessDaysIn` del backend, que es quien manda — esto es
 * solo para que el formulario pueda proponer las horas sin ida y vuelta. Si difirieran,
 * el backend rechaza el rango y el formulario mostraría un número que no se cumplió; por
 * eso las dos usan la MISMA definición (Lun-Vie + `company_holidays` activos).
 * ⚠ Fechas por componentes, nunca `new Date(str)`: es la trampa de zona de V46.
 */
function contarDiasHabiles(startStr, endStr, holidaySet) {
  if (!startStr || !endStr || endStr < startStr) return 0;
  const [ys, ms, ds] = startStr.split('-').map(Number);
  const [ye, me, de] = endStr.split('-').map(Number);
  let cur = Date.UTC(ys, ms - 1, ds);
  const end = Date.UTC(ye, me - 1, de);
  let n = 0;
  while (cur <= end) {
    const dt = new Date(cur);
    const dow = dt.getUTCDay();
    const key = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
    if (dow !== 0 && dow !== 6 && !holidaySet.has(key)) n++;
    cur += 86400000;
  }
  return n;
}

const label = { display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 };

export default function RequestCreatePage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [projects, setProjects] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [gangaMembers, setGangaMembers] = useState([]);
  const [gangaCargada, setGangaCargada] = useState(false);
  const [personas, setPersonas] = useState([]);   // V80: las decide el servidor, por proyecto
  const [origenLista, setOrigenLista] = useState(null); // 'ganga' | 'administrativo' | 'todas'
  const [personaId, setPersonaId] = useState('');
  const [availability, setAvailability] = useState(null);
  const [availLoading, setAvailLoading] = useState(false);

  const [type, setType] = useState('vacation');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [hours, setHours] = useState('');
  const [horasTocadas, setHorasTocadas] = useState(false);
  const [reason, setReason] = useState('');

  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [creada, setCreada] = useState(null);

  useEffect(() => {
    // my-projects ya viene scopeado por el backend: admin ve todos, el resto los de su ganga.
    client.get('/api/nomina/my-projects').then(r => setProjects(r.data || [])).catch(() => setProjects([]));
    client.get('/api/admin/holidays')
      .then(r => setHolidays((r.data || []).filter(h => h.is_active !== false)))
      .catch(() => setHolidays([]));
    // V80 — Las personas seleccionables ya NO se arman acá: las decide el servidor y se
    // piden por proyecto, abajo. Antes se traían todas "por las dudas" para poder caer al
    // fallback en el cliente, y esa regla duplicada es la que se separó del gate del saldo.
  }, []);

  // El proyecto define la ganga: de ahí salen las personas y los aprobadores.
  // ⚠ `gangaCargada` se baja ANTES del fetch: sin eso, entre que cambia el proyecto y
  // responde el servidor, la pantalla mostraría la cuadrilla del proyecto ANTERIOR — o
  // peor, caería al fallback de "todas" durante un instante y volvería.
  useEffect(() => {
    setGangaCargada(false);
    if (!projectId) { setGangaMembers([]); setPersonas([]); setOrigenLista(null); return; }
    let vigente = true;
    Promise.all([
      // La ganga sigue haciendo falta para la VISTA PREVIA DE LA CADENA (quién firma).
      client.get(`/api/nomina/projects/${projectId}/gangas`).catch(() => ({ data: [] })),
      // 🔴 V80 — Y las PERSONAS salen del servidor, con la misma función que usa el gate del
      // saldo (`personaSelectionService`). Antes la regla se calculaba acá —`usaTodas`— y el
      // servidor no la conocía: por eso el picker podía ofrecer a alguien cuyo saldo el
      // backend rechazaba. Pidiéndosela a él, eso es imposible por construcción.
      client.get('/api/nomina/employees', { params: { project_id: projectId, detalle: 1 } }).catch(() => ({ data: {} })),
    ]).then(([g, pers]) => {
      if (!vigente) return;
      setGangaMembers((g.data || []).flatMap(x => x.members || []));
      setPersonas(pers.data?.personas || []);
      setOrigenLista(pers.data?.origen || null);
      setGangaCargada(true);
    });
    return () => { vigente = false; };   // respuesta vieja que llega tarde no pisa la nueva
  }, [projectId]);

  // El saldo se consulta por persona. Un 403 no es un error de pantalla: significa que
  // quien mira no está habilitado a ver ese saldo, y el formulario sigue siendo usable.
  useEffect(() => {
    if (!personaId) { setAvailability(null); return; }
    const year = Number((startDate || new Date().toISOString().slice(0, 10)).slice(0, 4));
    setAvailLoading(true);
    // V80 — `project_id` viaja: el gate lo necesita para preguntar "¿conducís este proyecto
    // y esta persona es seleccionable acá?". Sin él no puede resolver y devuelve 403.
    client.get(`/api/vacation/availability?persona_id=${personaId}&year=${year}&project_id=${projectId}`)
      .then(r => setAvailability(r.data))
      .catch(() => setAvailability(null))
      .finally(() => setAvailLoading(false));
  }, [personaId, startDate, projectId]);

  const holidaySet = useMemo(
    () => new Set(holidays.map(h => String(h.date).slice(0, 10))), [holidays]);

  const diasHabiles = useMemo(
    () => contarDiasHabiles(startDate, endDate, holidaySet), [startDate, endDate, holidaySet]);

  // ⚠⚠ LAS HORAS SE DERIVAN DE LOS DÍAS, Y ES EL CORAZÓN DE ESTE FORMULARIO.
  // Medido en prod: vacaciones es casi todo múltiplo de 8 (74 de 8 h, 25 de 16 h, 12 de
  // 40 h; 6 excepciones), mientras enfermedad tiene horas sueltas (1, 2, 5.75, 8.5, 23…).
  // Con el total propuesto, el caso común de vacaciones no exige calcular nada — y el
  // campo sigue editable para el caso de enfermedad. Un solo formulario, sin modos.
  // La derivación corre al cambiar FECHAS; escribir horas no recalcula el rango.
  useEffect(() => {
    if (!horasTocadas) setHours(diasHabiles > 0 ? String(diasHabiles * HORAS_POR_DIA) : '');
  }, [diasHabiles, horasTocadas]);

  const feriadosEnRango = useMemo(() => {
    if (!startDate || !endDate) return [];
    return holidays.filter(h => {
      const d = String(h.date).slice(0, 10);
      return d >= startDate && d <= endDate;
    });
  }, [startDate, endDate, holidays]);

  // ⚠⚠ LA REGLA DE QUIÉN ES SELECCIONABLE SE MUDÓ AL BACKEND (V80).
  // Vivía acá como `usaTodas` —cuadrilla si la hay, todas si no, y todas siempre en
  // administrativo— y el servidor no la conocía: por eso el gate del saldo, que tenía su
  // propia versión, rechazaba a gente que este picker ofrecía. Ahora la decide
  // `personaSelectionService` y esta pantalla la CONSUME; el `origen` que devuelve es lo que
  // elige el mensaje de abajo.
  //
  // 🔴 La distinción de V69 sigue viva y sigue importando, solo que ahora la nombra el
  // servidor: ADMINISTRATIVO es el alcance correcto POR DISEÑO (no hay cuadrilla de donde
  // sacar la cadena; el socio sale de la persona), mientras que un proyecto de OBRA sin
  // cuadrilla es DESCONFIGURACIÓN. Confundirlos hace que el segundo se lea como normal.
  //
  // `proyectoAdmin` sobrevive porque decide OTRA cosa: de dónde sale el socio que firma.
  const proyectoAdmin = useMemo(
    () => projects.find(p => String(p.id) === String(projectId))?.project_type === 'administrative',
    [projects, projectId]
  );
  const personaOptions = useMemo(
    () => personas.map(p => ({ id: String(p.id), label: p.name, sublabel: p.external_id || '' })),
    [personas]
  );

  const personaName = personaOptions.find(o => o.id === String(personaId))?.label || null;

  // Vista previa de la cadena, resuelta de la MISMA ganga que usa el backend.
  const ingenieros = gangaMembers.filter(m => m.role === 'engineer').map(m => m.user_name).filter(Boolean);
  const socios     = gangaMembers.filter(m => m.role === 'socio');

  // 🔴 V69 — EN ADMINISTRATIVO LA PREGUNTA ES POR LA PERSONA, NO POR EL PROYECTO.
  // El bug que encontró el pase visual: este panel avisaba "Este proyecto no tiene
  // Socio(a) en la ganga" sobre solicitudes que el backend ACEPTABA sin problema. Era un
  // AVISO FALSO, no un bloqueo: la lógica de acá seguía siendo la vieja —mirar la ganga—
  // mientras el servidor ya resolvía por `personas.pto_socio_user_id`.
  //
  // ⚠ Un aviso anticipado que contradice al servidor es PEOR que no tener aviso: enseña a
  // ignorarlo, y el día que diga la verdad tampoco le van a creer. Así que o dice lo mismo
  // que el backend, o no dice nada.
  const personaElegida = personas.find(p => String(p.id) === String(personaId));
  const socioDeLaPersona = personaElegida?.pto_socio_user_id ?? null;

  const soySocioDeEsteProyecto = proyectoAdmin
    ? socioDeLaPersona === user?.id
    : socios.some(m => m.user_id === user?.id);
  // En administrativo el aviso espera a que haya PERSONA elegida: sin ella no hay nada que
  // saber todavía, y avisar antes sería inventar.
  const sinSocio = proyectoAdmin
    ? (!!personaId && !socioDeLaPersona)
    : (!!projectId && socios.length === 0);

  const horasNum = Number(hours) || 0;
  const disp = availability?.[type];
  const excedeSaldo = disp && horasNum > Number(disp.available);
  const todayStr = new Date().toISOString().slice(0, 10);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!projectId)  return setError('Elegí el proyecto: de ahí sale la cadena de aprobación.');
    if (!personaId)  return setError('Elegí de quién es la ausencia.');
    if (!startDate || !endDate) return setError('Elegí las fechas de inicio y fin.');
    if (diasHabiles <= 0) return setError('El rango seleccionado no contiene días hábiles.');
    if (!(horasNum > 0)) return setError('Las horas deben ser mayores que cero.');

    setSubmitting(true);
    try {
      const { data } = await client.post('/api/vacation/requests', {
        persona_id: Number(personaId),
        project_id: Number(projectId),
        type,
        start_date: startDate,
        end_date: endDate,
        hours_requested: horasNum,
        reason: reason || null,
      });
      setCreada(data);
    } catch (err) {
      appInsights?.trackException({
        exception: err,
        properties: { context: 'pto_request_create', pto_type: type, hours_requested: horasNum },
      });
      setError(err.response?.data?.error || 'Error al crear la solicitud.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Pantalla de resultado ───────────────────────────────────────────────────
  // Un solo camino después de enviar, para que el aviso de saldo no se pierda en una
  // navegación automática: la solicitud YA se creó aunque el saldo quede negativo.
  if (creada) {
    return (
      <div className="page-pad" style={{ padding: '20px 24px', maxWidth: 620, margin: '0 auto' }}>
        <div style={{ ...bubbleStyle, padding: '22px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <CheckCircle2 size={20} color="#16a34a" />
            <h2 style={{ fontSize: 17, fontWeight: 700, color: NAVY, margin: 0 }}>
              {creada.auto_approved ? 'Ausencia registrada y aprobada' : 'Solicitud enviada'}
            </h2>
          </div>
          <p style={{ fontSize: 13, color: '#475569', margin: '0 0 12px', lineHeight: 1.5 }}>
            {creada.auto_approved
              ? 'Quedó aprobada en el acto porque la registró un Socio(a) del proyecto, que es quien cierra la cadena.'
              : 'Queda pendiente de aprobación. Se notificó a quien tiene que revisarla.'}
          </p>
          {creada.warning && (
            <div style={{
              display: 'flex', gap: 8, alignItems: 'flex-start',
              background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
              padding: '10px 14px', fontSize: 12.5, color: '#92400e', marginBottom: 14, lineHeight: 1.5,
            }}>
              <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{creada.warning}</span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => navigate(`/vacaciones/${creada.id}`)} style={navyBtn}>Ver solicitud</button>
            <button
              onClick={() => { setCreada(null); setStartDate(''); setEndDate(''); setHours(''); setHorasTocadas(false); setReason(''); }}
              style={{ ...navyBtn, background: '#fff', color: NAVY, border: `1px solid ${BORDER_COLOR}` }}
            >Nueva solicitud</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-pad" style={{ padding: '20px 24px', maxWidth: 620, margin: '0 auto' }}>
      <button
        onClick={() => navigate('/vacaciones')}
        style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: 13, marginBottom: 12, fontFamily: 'Inter, sans-serif' }}
      >
        <ArrowLeft size={16} /> Volver a solicitudes
      </button>

      <h1 style={{ fontSize: 20, fontWeight: 700, color: NAVY, margin: '0 0 16px' }}>Nueva Solicitud</h1>

      <PTOBalanceCard availability={availability} loading={availLoading} personaName={personaName} />

      <form onSubmit={handleSubmit} style={{ ...bubbleStyle, padding: '20px 24px' }}>
        {/* 1 — Proyecto */}
        <div style={{ marginBottom: 16 }}>
          <label style={label}>Proyecto</label>
          <select
            value={projectId}
            onChange={e => { setProjectId(e.target.value); setPersonaId(''); }}
            style={selectStyle}
          >
            <option value="">— Seleccioná el proyecto —</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{etiquetaProyecto(p.code, p.name)}</option>
            ))}
          </select>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
            De acá sale la cadena de aprobación de la ausencia.
          </div>
        </div>

        {/* 2 — Persona */}
        <div style={{ marginBottom: 16 }}>
          <label style={label}>¿De quién es la ausencia?</label>
          <SearchableSelect
            items={personaOptions}
            value={personaId ? String(personaId) : ''}
            onChange={v => setPersonaId(v)}
            disabled={!projectId}
            placeholder={projectId ? 'Buscar persona…' : 'Elegí primero el proyecto'}
          />
          {/* Informativo, NO accionable: dice de dónde sale la lista. */}
          {projectId && gangaCargada && (
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
              {/* V80 — El mensaje lo decide el ORIGEN que informa el servidor, no una regla
                  recalculada acá. Los tres casos siguen siendo distintos a propósito (V69):
                  administrativo es POR DISEÑO y no se lee como una falta que alguien deba
                  arreglar; "sin cuadrilla" sí es desconfiguración y el texto lo dice. */}
              {origenLista === 'ganga'
                ? `${personaOptions.length} en la cuadrilla de este proyecto.`
                : origenLista === 'administrativo'
                  ? `Proyecto administrativo: no lleva cuadrilla, la lista es toda la empresa (${personaOptions.length}).`
                  : `Sin cuadrilla asignada — mostrando todas las personas activas (${personaOptions.length}).`}
            </div>
          )}
        </div>

        {/* 3 — Tipo */}
        <div style={{ marginBottom: 16 }}>
          <label style={label}>Tipo de ausencia</label>
          <select value={type} onChange={e => setType(e.target.value)} style={selectStyle}>
            {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {/* 4 — Rango */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={label}>Desde</label>
            <input
              type="date" value={startDate} style={fieldStyle} min={todayStr}
              onChange={e => { setStartDate(e.target.value); if (endDate && e.target.value > endDate) setEndDate(e.target.value); }}
            />
          </div>
          <div>
            <label style={label}>Hasta</label>
            <input
              type="date" value={endDate} style={fieldStyle} min={startDate || todayStr}
              onChange={e => setEndDate(e.target.value)}
            />
          </div>
        </div>

        {/* 5 — Horas derivadas */}
        <div style={{ marginBottom: 16 }}>
          <label style={label}>Horas</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12.5, color: '#64748b' }}>
              {diasHabiles > 0
                ? `${diasHabiles} día${diasHabiles !== 1 ? 's' : ''} hábil${diasHabiles !== 1 ? 'es' : ''} × ${HORAS_POR_DIA} h =`
                : 'Elegí el rango'}
            </span>
            <input
              type="number" step="0.25" min="0" value={hours} style={{ ...fieldStyle, width: 120 }}
              onChange={e => { setHorasTocadas(true); setHours(e.target.value); }}
            />
            {horasTocadas && diasHabiles > 0 && (
              <button
                type="button"
                onClick={() => { setHorasTocadas(false); setHours(String(diasHabiles * HORAS_POR_DIA)); }}
                style={{ background: 'none', border: 'none', color: AMBER, cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'Inter, sans-serif' }}
              >Restaurar</button>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
            Se propone el día completo. Editá el total para media jornada o una cita médica.
          </div>
          {feriadosEnRango.length > 0 && (
            <div style={{ marginTop: 6, fontSize: 11.5, color: '#0369a1' }}>
              Feriados excluidos ({feriadosEnRango.length}):{' '}
              {feriadosEnRango.map((h, i) => (
                <span key={i}>
                  {h.name} ({formatDateSafe(h.date, { day: '2-digit', month: 'short' })}){i < feriadosEnRango.length - 1 ? ', ' : ''}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Aviso de saldo — NO bloquea (V62) */}
        {excedeSaldo && (
          <div style={{
            display: 'flex', gap: 8, alignItems: 'flex-start',
            background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
            padding: '10px 14px', fontSize: 12.5, color: '#92400e', marginBottom: 16, lineHeight: 1.5,
          }}>
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              Se piden <strong>{horasNum} h</strong> y hay <strong>{disp.available} h</strong> disponibles.
              Se puede enviar igual — el saldo queda en negativo.
            </span>
          </div>
        )}

        {/* Vista previa de la cadena */}
        {projectId && (
          <div style={{
            background: '#f8fafc', border: `1px solid ${BORDER_COLOR}`, borderRadius: 8,
            padding: '10px 14px', fontSize: 12, color: '#475569', marginBottom: 16, lineHeight: 1.6,
          }}>
            {soySocioDeEsteProyecto ? (
              <><strong style={{ color: '#16a34a' }}>Se aprueba automáticamente.</strong>{' '}
                {proyectoAdmin
                  ? 'Sos el Socio(a) asignado de esta persona, así que la ausencia queda aprobada al registrarla.'
                  : 'Sos Socio(a) de este proyecto, así que la ausencia queda aprobada al registrarla.'}</>
            ) : sinSocio ? (
              <><strong style={{ color: '#dc2626' }}>
                {proyectoAdmin
                  ? 'Esta persona no tiene Socio(a) asignado.'
                  : 'Este proyecto no tiene Socio(a) en la ganga.'}</strong>{' '}
                {proyectoAdmin
                  ? 'La solicitud no se va a poder enviar hasta que se le asigne uno en Gestión de Empleados.'
                  : 'La solicitud no se va a poder enviar hasta que se configure.'}</>
            ) : proyectoAdmin ? (
              // Un solo nivel, y se nombra a quien firma. No se lista ingeniero: en
              // administrativo ese nivel no existe (ver `resolveLevelApprovers`).
              <>Aprobación: <strong>{
                personaElegida?.pto_socio_name || 'el Socio(a) asignado de esta persona'
              }</strong> (cierra) — proyecto administrativo, un solo nivel.</>
            ) : (
              <>Aprobación: <strong>{ingenieros.length ? ingenieros.join(' o ') : 'sin ingeniero(a) — se saltea'}</strong>
                {' → '}<strong>{socios.map(s => s.user_name).filter(Boolean).join(' o ')}</strong> (cierra)</>
            )}
          </div>
        )}

        <div style={{ marginBottom: 20 }}>
          <label style={label}>Motivo <span style={{ fontWeight: 400, color: '#94a3b8' }}>(opcional)</span></label>
          <textarea
            value={reason} onChange={e => setReason(e.target.value)} rows={3}
            placeholder="Ej: cita médica, viaje familiar"
            style={{ ...fieldStyle, resize: 'vertical' }}
          />
        </div>

        {error && (
          <div style={{
            background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca',
            borderRadius: 6, padding: '8px 12px', fontSize: 13, marginBottom: 16,
          }}>{error}</div>
        )}

        <button
          type="submit" disabled={submitting}
          style={{ ...navyBtn, width: '100%', opacity: submitting ? 0.6 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}
        >
          {submitting ? 'Enviando…' : 'Enviar solicitud'}
        </button>
      </form>
    </div>
  );
}
